import { useState } from 'preact/hooks';
import { useAsyncAction } from '../../hooks/use-async-action';
import { bulkDeleteProducts } from '../../api/products';
import { Card } from '../../components/card';
import { Button } from '../../components/button';
import { Input } from '../../components/input';
import { Select } from '../../components/select';
import { Textarea } from '../../components/textarea';
import { Checkbox } from '../../components/checkbox';
import { Message } from '../../components/message';

interface BulkDeleteProps {
  onComplete: () => void;
}

const SOURCE_OPTIONS = [
  { value: '', label: 'Any Source' },
  { value: 'openfoodfacts', label: 'openfoodfacts' },
  { value: 'manual', label: 'manual' },
  { value: 'user_submitted', label: 'user_submitted' },
];

export function BulkDelete({ onComplete }: BulkDeleteProps) {
  const [expanded, setExpanded] = useState(false);
  const [before, setBefore] = useState('');
  const [source, setSource] = useState('');
  const [barcodes, setBarcodes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const action = useAsyncAction(async () => {
    const barcodeList = barcodes
      .split('\n')
      .map((b) => b.trim())
      .filter(Boolean);

    const res = await bulkDeleteProducts({
      before: before || undefined,
      source: source || undefined,
      barcodes: barcodeList.length > 0 ? barcodeList : undefined,
      confirm: true,
    });

    setSuccessMsg(
      `Deleted ${res.deleted.products} products, ${res.deleted.ingredients} ingredients, ${res.deleted.verdicts} verdicts, ${res.deleted.scans} scans`,
    );
    setBefore('');
    setSource('');
    setBarcodes('');
    setConfirmed(false);
    onComplete();
  });

  const hasFilter = before || source || barcodes.trim();

  return (
    <Card className="bulk-delete-card">
      <div class="bulk-delete-header" onClick={() => setExpanded(!expanded)}>
        <div class="bulk-delete-header-left">
          <div class="bulk-delete-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </div>
          <div>
            <div class="bulk-delete-title">Bulk Delete</div>
            <div class="bulk-delete-desc">Delete multiple products matching filters</div>
          </div>
        </div>
        <svg class={`bulk-delete-chevron ${expanded ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {expanded && (
        <div class="bulk-delete-body">
          {successMsg && <Message text={successMsg} type="success" onDismiss={() => setSuccessMsg(null)} />}
          {action.error && <Message text={action.error} type="error" />}

          <div class="form-grid">
            <div class="form-row">
              <label>Created before</label>
              <Input
                type="date"
                value={before}
                onChange={setBefore}
                placeholder="YYYY-MM-DD"
              />
            </div>

            <div class="form-row">
              <label>Source</label>
              <Select value={source} onChange={setSource} options={SOURCE_OPTIONS} />
            </div>
          </div>

          <div class="form-row" style={{ marginBottom: 'var(--sp-4)' }}>
            <label>Barcodes (one per line)</label>
            <Textarea
              value={barcodes}
              onChange={setBarcodes}
              placeholder="Enter barcodes, one per line"
              rows={4}
            />
          </div>

          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <Checkbox
              checked={confirmed}
              onChange={setConfirmed}
              label="I understand this is irreversible"
              id="bulk-delete-confirm"
            />
          </div>

          <Button
            variant="danger"
            size="sm"
            onClick={() => action.execute()}
            disabled={!confirmed || !hasFilter || action.loading}
          >
            {action.loading ? 'Deleting...' : 'Delete Matching Products'}
          </Button>
        </div>
      )}
    </Card>
  );
}
